import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton, SvgIcon } from '@salecycle/ui-component-library';
import { PropsWithChildren, useState } from 'react';
import styled from 'styled-components';
import { useOnBranchRemove } from '../hooks/useOnBranchRemove';
import { ConfirmChangeBrandModal } from './BranchConfirmDeletionModal';
import { useTranslation } from 'react-i18next';

export interface BranchDragCardProps {
  id: string;
}

const BranchBox = styled.div`
  background: ${({ theme }) => theme.designToken.baseWhite};
  padding: ${({ theme }) => theme.designToken.spacing3} ${({ theme }) => theme.designToken.spacing4};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing3};
  flex-grow: 1;
  max-width: 264px;
  min-width: 264px;
`;

const BranchControls = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
  justify-content: space-between;
`;

const BranchCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  > button:first-child {
    margin-right: ${({ theme }) => theme.designToken.spacing2};
  }
`;

export const BranchDragCard = ({ id, children }: PropsWithChildren<BranchDragCardProps>) => {
  const { t } = useTranslation('campaigns');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const removeBranch = useOnBranchRemove();
  const [isBranchModalOpen, setBranchModal] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <>
      <BranchBox ref={setNodeRef} style={style}>
        <BranchControls>
          <SvgIcon
            name="grip-dots-vertical"
            aria-label={t('onsite.editor.workflow.dragMessageSidebarAria')}
            size="24"
            style={{ padding: 0, cursor: isDragging ? 'grabbing' : 'grab' }}
            {...attributes}
            {...listeners}
          />
          <BranchCardHeader>
            <IconButton
              ariaLabel={t('onsite.editor.workflow.branchCardDeleteAria')}
              icon="trash"
              onClick={() => setBranchModal(true)}
            />
            <IconButton
              ariaLabel={t('onsite.editor.workflow.branchCardToggleAria')}
              icon={showContent ? 'angle-down' : 'angle-up'}
              onClick={() => setShowContent(!showContent)}
              svgIcon
            />
          </BranchCardHeader>
        </BranchControls>
        {showContent && children}
      </BranchBox>
      <ConfirmChangeBrandModal
        openModal={isBranchModalOpen}
        setIsModalOpen={setBranchModal}
        confirmModal={() => removeBranch(id)}
      />
    </>
  );
};
